import {IconButton, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {memo, useCallback, useMemo} from 'react';

import {useActionLocksForRow} from '../../../../../../store/selectors/useActionLocksForRow';
import {ActionDefinitionObject} from '../../../../../../types/ActionDefinition';
import {ActionsAlignment} from '../../../../../../types/ActionsAlignment';
import {RowData} from '../../../../../../types/RowData';
import {getActionButtonSize} from '../../../../../../utils/getActionButtonSize';
import {getActionsJustifyContent} from '../../../../../../utils/getActionsJustifyContent';
import {getRowPaddingClass, LineHeight} from '../../../../../../utils/lineHeightToValue';
import {partitionRowActions} from '../../../../../../utils/partitionRowActions';
import {ActionColumnCell} from './components/ActionColumnCell/ActionColumnCell';
import {RowActionButton} from './components/RowActionButton/RowActionButton';
import {RowActionsOverflow} from './components/RowActionsOverflow/RowActionsOverflow';

export type RowActionsProps = {
  row: RowData;
  rowIndex: number;
  actionsDefinition: ActionDefinitionObject;
  onActionClick: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  isMobile: boolean;
  rowHeight: LineHeight;
  actionColumnWidth: number;
  heightPx: number;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isStriped?: boolean;
  isDisabled?: boolean;
  actionsAlignment?: ActionsAlignment | null;
  horizontalSeparators: boolean;
  /** Mobile only: opens the MobileRowActionSheet for this row instead of rendering inline buttons. */
  onOpenActionSheet?: (row: RowData) => void;
};

// memo: rendered N times (one per visible row); only re-renders when own row's actions change.
export const RowActions = memo((props: RowActionsProps) => {
  const t = useTranslationContext();
  // useMemo guarantees that iconActions/overflowActions keep referential identity across
  // re-renders caused by lockedActionKeys updates. Without this, the per-action onClick
  // closures below were rebuilt on every lock/unlock, and the resulting prop churn on
  // <IconButton>/<RowActionsOverflow> compounded into a Maximum-update-depth loop once
  // a click triggered a lockAction → re-render → fresh handlers → lock cycle.
  const {iconActions, overflowActions, rowClickAction} = useMemo(
    () => partitionRowActions(props.row.actions, props.actionsDefinition),
    [props.row.actions, props.actionsDefinition]
  );
  const buttonSize = getActionButtonSize(props.rowHeight, props.isMobile);
  const paddingClass = getRowPaddingClass[props.rowHeight];
  const lockedActionKeys = useActionLocksForRow(props.row.id);

  const onActionClick = props.onActionClick;
  const row = props.row;

  const handleIconActionClick = useCallback(
    (actionKey: string, event: React.MouseEvent) => {
      onActionClick(actionKey, row, event);
    },
    [onActionClick, row]
  );

  const handleOverflowActionClick = useCallback(
    (actionKey: string, nativeEvent: MouseEvent) => {
      onActionClick(actionKey, row, {
        stopPropagation: () => undefined,
        nativeEvent,
      } as React.MouseEvent);
    },
    [onActionClick, row]
  );

  const hasActions =
    iconActions.length > 0 || overflowActions.length > 0 || rowClickAction !== null;
  const desktopIconActions = iconActions.filter((action) => action.definition.icon.value);
  const desktopRowClickAction = rowClickAction?.definition.icon.value ? rowClickAction : null;
  const justifyContent = getActionsJustifyContent(props.actionsAlignment);

  // Mobile: collapse all per-row inline buttons into a single ⋮ trigger that opens the
  // MobileRowActionSheet (owned by GridBody, not rendered per-row). All hooks above this
  // point still run unconditionally on every render, mobile or not.
  if (props.isMobile && props.onOpenActionSheet) {
    const openActionSheet = props.onOpenActionSheet;

    return (
      <ActionColumnCell
        width={props.actionColumnWidth}
        heightPx={props.heightPx}
        rowIndex={props.rowIndex}
        horizontalSeparators={props.horizontalSeparators}
        isSelected={props.isSelected}
        isHighlighted={props.isHighlighted}
      >
        <Show when={hasActions}>
          <div className="flex items-center h-full" style={{justifyContent}}>
            <IconButton
              size={buttonSize}
              icon="navigation/more_vert"
              aria-label={t('page.dataGrid.row.actionsAriaLabel')}
              isDisabled={props.isDisabled === true}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                openActionSheet(row);
              }}
              data-testid={`button-rowAction.overflow-${row.id}`}
            />
          </div>
        </Show>
      </ActionColumnCell>
    );
  }

  return (
    <ActionColumnCell
      width={props.actionColumnWidth}
      heightPx={props.heightPx}
      rowIndex={props.rowIndex}
      horizontalSeparators={props.horizontalSeparators}
      isSelected={props.isSelected}
      isHighlighted={props.isHighlighted}
    >
      <Show when={hasActions}>
        <div className={`flex items-center h-full gap-1 ${paddingClass}`} style={{justifyContent}}>
          {desktopIconActions.map((action) => (
            <RowActionButton
              key={action.actionKey}
              action={action}
              buttonSize={buttonSize}
              isDisabled={props.isDisabled === true || lockedActionKeys.has(action.actionKey)}
              onClick={handleIconActionClick}
            />
          ))}
          <RowActionsOverflow
            overflowActions={overflowActions}
            onActionClick={handleOverflowActionClick}
            rowId={props.row.id}
            buttonSize={buttonSize}
            isDisabled={props.isDisabled === true}
            lockedActionKeys={lockedActionKeys}
          />
          {/* Trailing chevron for a rowClick-only action: whole-row click also fires it,
              but this gives users a visible affordance. */}
          <Show when={desktopRowClickAction}>
            {(desktopRowClickAction) => (
              <RowActionButton
                action={desktopRowClickAction}
                buttonSize={buttonSize}
                isDisabled={
                  props.isDisabled === true || lockedActionKeys.has(desktopRowClickAction.actionKey)
                }
                onClick={handleIconActionClick}
              />
            )}
          </Show>
        </div>
      </Show>
    </ActionColumnCell>
  );
});
